<template>
  <lazy-component
    ref="videoRoot"
    class="video-lazy-wrapper"
    :class="[ratio ? `ratio ratio-${ratio}` : '', { 'full-height': fullHeight }]"
    @intersected="onShow"
  >
    <div
      ref="videoWrapper"
      class="video-wrapper"
      :class="{ initialized: !!player, 'non-interactive': !interactive }"
      @click="play"
      @touchstart="play"
    >
      <video
        :id="`video-container-${id}`"
        ref="videoPlayer"
        class="video-js"
        :class="{ 'no-preview': !hasPreview }"
        preload="auto"
        :poster="poster"
        crossorigin="anonymous"
        playsinline
        :muted="muted"
      >
        <source v-if="videoSource" :src="videoSource" :type="videoSourceType" />
        <track
          v-for="subtitle in subtitles"
          :key="subtitle.lang"
          kind="subtitles"
          :src="subtitle.src"
          :srclang="subtitle.lang"
          :label="subtitle.label"
          :default="locale === subtitle.lang ? 'true' : false"
        />
      </video>

      <slot />
    </div>
  </lazy-component>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useUserStore } from "@/store/user";
import { useVideoStore } from "@/store/video";
import { usePlayerStore } from "@/store/player";
import { getMediaUrl } from "@/utils/mediaHelper";
import { amplitudeVideoTrack } from "@/utils/amplitude";
import { ENV_PRODUCTION, ROLE_CENSORED_TOKEN } from "@/utils/variables";
import videojs from "video.js";
import "videojs-quality-selector-hls";
import "videojs-mux";
import "videojs-mobile-ui";
import "videojs-hotkeys";
import { insertScript } from "@/utils/domHelper";
import seekButtons from "@/utils/playerHelpers/seekButtons";
import { useI18n } from "vue-i18n";
import { useRoleStore } from "@/store/role.ts";
import { getCensoredVideoSource } from "@/utils/censorHelper.ts";
import { useRoute } from "vue-router";

defineOptions({
  name: "player",
});

const props = defineProps({
  id: {
    type: [Number, String],
    required: false,
    default: null,
  },
  interactive: {
    type: Boolean,
    required: false,
    default: true,
  },
  autoplay: {
    type: Boolean,
    required: false,
    default: false,
  },
  source: {
    type: String,
    required: false,
    default: null,
  },
  sourceType: {
    type: String,
    required: false,
    default: "application/x-mpegURL",
  },
  poster: {
    type: String,
    default: null,
  },
  subtitlesEn: {
    type: [String, null],
    required: false,
    default: null,
  },
  subtitlesDe: {
    type: [String, null],
    required: false,
    default: null,
  },
  subtitlesFr: {
    type: [String, null],
    required: false,
    default: null,
  },
  ratio: {
    type: String,
    default: "",
    validator(value: string) {
      return ["", "16x9", "4x3"].includes(value);
    },
  },
  fullHeight: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasPreview: {
    type: Boolean,
    required: false,
    default: false,
  },
  trackPlayback: {
    type: Boolean,
    required: false,
    default: false,
  },
  cast: {
    type: Boolean,
    required: false,
    default: true,
  },
});

declare global {
  interface Window {
    cast?: any;
    chrome?: any;
    videojs?: any;
  }
}

const { locale } = useI18n();

const emit = defineEmits(["play", "ready", "sendPlayback"]);
const userStore = useUserStore();
const videoStore = useVideoStore();
const playerStore = usePlayerStore();
const roleStore = useRoleStore();
const route = useRoute();

const videoRoot = ref<HTMLElement | null>(null);
const videoWrapper = ref<HTMLElement | null>(null);
const videoPlayer = ref<HTMLElement | null>(null);
const player = ref<any>(null);
const currentTextTrackIndex = ref<number | null>(null);
const started = ref(false);
const log = ref({
  timerStart: 0,
  currentTime: 0,
  previousTime: 0,
  playbackSent: false,
});

const activeVideo = computed(() => videoStore.getActiveVideo);
const profile = computed(() => userStore.getProfile);
const quality = computed(() => playerStore.getQuality);
const volume = computed(() => playerStore.getVolume);
const muted = computed(() => !!route.query?.ts);

const isCensored = computed(() => roleStore.hasRole(ROLE_CENSORED_TOKEN));
const videoSource = computed(() => (isCensored.value ? getCensoredVideoSource() : props.source));
const videoSourceType = computed(() => (isCensored.value ? "application/x-mpegURL" : props.sourceType));

const subtitles = computed(() => {
  const subtitles: Record<string, any>[] = [];

  if (!props.interactive) {
    return subtitles;
  }

  if (props.subtitlesEn) {
    subtitles.push({ label: "English", lang: "en", src: getSubtitles(props.subtitlesEn) });
  }

  if (props.subtitlesFr) {
    subtitles.push({ label: "French", lang: "fr", src: getSubtitles(props.subtitlesFr) });
  }

  if (props.subtitlesDe) {
    subtitles.push({ label: "Deutsch", lang: "de", src: getSubtitles(props.subtitlesDe) });
  }

  return subtitles;
});

defineExpose({
  log,
  subtitles,
  currentTextTrackIndex,
});

watch(
  () => props.poster,
  (newPoster) => {
    if (player.value) {
      player.value.poster(newPoster);
    }
  },
);

watch(activeVideo, (newVal, oldVal) => {
  if (oldVal === props.id && player.value) {
    player.value.pause();
    sendPlayback();
  }
});

watch(currentTextTrackIndex, (val) => {
  if (!window.cast) {
    return;
  }

  const mediaSession = window.cast.framework.CastContext.getInstance().getCurrentSession()?.getMediaSession();

  if (!mediaSession) {
    return;
  }

  const tracksInfoRequest = new window.chrome.cast.media.EditTracksInfoRequest(val === null ? [] : [val]);
  mediaSession.editTracksInfo(tracksInfoRequest, null, null);
});

const play = async () => {
  if (!props.source) {
    return;
  }

  emit("play");

  if (videoPlayer.value) {
    videoPlayer.value.focus({ preventScroll: true });
  }
};

const getQualityFromButton = (el: HTMLElement) => {
  const numberPattern = /\d+/g;
  const qualityText = (el.querySelector(".vjs-menu-item-text") as HTMLElement)?.innerText || "";
  const matches = qualityText.match(numberPattern);
  return qualityText === "Auto" ? qualityText.toLowerCase() : parseInt(matches ? matches.join("") : "0");
};

const load = () => {
  if (player.value || !videoPlayer.value) {
    return;
  }

  const seekStep = 10;

  let options: Record<string, any> = {
    controls: true,
    errorDisplay: false,
    disablePictureInPicture: true,
    techOrder: ["html5"],
    plugins: {
      hotkeys: {
        volumeStep: 0.1,
        seekStep: seekStep,
        enableModifiersForNumbers: false,
        enableVolumeScroll: false,
      },
      mobileUi: {
        fullscreen: {
          enterOnRotate: true,
          lockOnRotate: !videojs.browser.IS_IOS,
          lockToLandscapeOnEnter: !videojs.browser.IS_IOS,
        },
      },
    },
    html5: {
      nativeAudioTracks: false,
      nativeVideoTracks: false,
      vhs: {
        overrideNative: !videojs.browser.IS_IOS,
        handlePartialData: true,
      },
    },
    controlBar: {
      pictureInPictureToggle: false,
    },
    poster: props.poster,
  };

  if (videojs.getPlugin("chromecast") !== undefined && window.cast) {
    options = setChromecastOptions(options);
  }

  if (import.meta.env.VITE_MUX_ENABLED && props.interactive && profile?.value) {
    options = setMuxOptions(options);
  }

  player.value = videojs(videoPlayer.value, options, () => {
    nextTick(() => {
      const posterImg = videoWrapper.value?.querySelector(".vjs-poster img");

      if (posterImg) {
        posterImg.setAttribute("fetchpriority", "high");
        posterImg.setAttribute("loading", "eager");
      }

      const touchOverlay = videoWrapper.value?.querySelector(".vjs-touch-overlay");

      if (touchOverlay) {
        const observer = new MutationObserver(() => {
          if (touchOverlay.classList.length === 1 && !player.value.paused()) {
            player.value.controlBar.addClass("hide");
          } else {
            player.value.controlBar.removeClass("hide");
          }
        });

        observer.observe(touchOverlay, {
          attributes: true,
          attributeFilter: ["class"],
          childList: false,
          characterData: false,
        });
      }

      seekButtons(player.value, seekStep);

      emit("ready", player.value);
    });
  });

  player.value.one("canplay", () => {
    player.value.volume(volume.value);

    if (route.query?.ts) {
      player.value.currentTime(parseInt(route.query.ts as string));
    }
  });

  if (player.value.airPlay !== undefined) {
    player.value.airPlay();
  }

  if (player.value.chromecast !== undefined) {
    player.value.chromecast();
  }

  player.value.one("play", async () => {
    started.value = true;
    amplitudeVideoTrack("video starts playing", props.id);
  });

  player.value.on("play", async () => {
    log.value.playbackSent = false;
    log.value.timerStart = player.value.currentTime();
    window.addEventListener("beforeunload", sendPlayback);

    videoStore.setActiveVideo(props.id);
  });

  player.value.on("texttrackchange", () => {
    const textTracks = player.value.textTracks();
    const subtitleTracks = [];

    for (let i = 0; i < textTracks.length; i++) {
      if (textTracks[i].kind !== "subtitles") {
        continue;
      }

      subtitleTracks.push(textTracks[i]);
    }

    for (let i = 0; i < subtitleTracks.length; i++) {
      if (subtitleTracks[i].mode === "showing") {
        currentTextTrackIndex.value = i;
        return;
      }
    }

    currentTextTrackIndex.value = null;
  });

  if (!videojs.browser.IS_IOS) {
    const qualitySelector = player.value.qualitySelectorHls({ displayCurrentQuality: true });

    player.value.one("loadedmetadata", () => {
      let currentQualityButton: Element | null = null;
      const qlt = quality.value !== "auto" ? parseInt(quality.value.toString()) : "auto";
      videoWrapper.value?.querySelectorAll(".vjs-quality-selector .vjs-menu-item").forEach((el) => {
        if (qlt === getQualityFromButton(el as HTMLElement)) {
          currentQualityButton = el;
        }

        el.addEventListener("click", (ev) => {
          const qlt = getQualityFromButton(ev.currentTarget as HTMLElement);
          playerStore.setQuality(qlt);
        });
      });

      if (!currentQualityButton) {
        return;
      }

      (videoWrapper.value?.querySelector(".vjs-quality-selector .vjs-selected") as HTMLElement).classList.remove("vjs-selected");
      (currentQualityButton as HTMLElement).classList.toggle("vjs-selected", true);
      qualitySelector.setQuality(qlt);
    });
  }

  player.value.on("timeupdate", () => {
    log.value.previousTime = log.value.currentTime;
    log.value.currentTime = player.value.currentTime();
  });

  player.value.on("volumechange", () => {
    const vlm = player.value.muted() ? 0 : parseFloat(player.value.volume());
    playerStore.setVolume(vlm);
  });

  player.value.on("seeking", () => {
    if (!log.value.playbackSent) {
      sendPlayback();
    }
  });

  player.value.on("ended", () => {
    sendPlayback();
  });

  player.value.on("error", async () => {
    try {
      const response = await fetch(videoSource.value, { method: "HEAD" });

      if (response.status === 404) {
        window.bugsnag?.notify(new Error("Video source not found (404)"), (event) => {
          event.severity = "error";
          event.context = "video-player";
          event.addMetadata("video", {
            id: props.id,
            source: videoSource.value,
            sourceType: videoSourceType.value,
          });
        });
      }
    } catch (fetchError: any) {
      window.bugsnag?.notify(fetchError);
    }
  });

  if (videojs.browser.IS_IOS && videojs.browser.IS_CHROMIUM) {
    return;
  }

  if (props.autoplay && player.value.paused()) {
    setTimeout(() => {
      player.value.play().catch(() => {});
    }, 100);
  }
};

const sendPlayback = () => {
  if (!props.trackPlayback) {
    return;
  }

  if (log.value.playbackSent) {
    return;
  }

  const duration = log.value.previousTime - log.value.timerStart;

  if (Math.floor(duration) <= 0) {
    return;
  }

  emit("sendPlayback", {
    duration: duration,
    startDate: Math.floor(Date.now() / 1000),
    timelineStart: log.value.timerStart,
  });
};

const onShow = async () => {
  if (!props.source) {
    return;
  }

  (videojs.log as any).warn = function () {};

  if (!window.videojs) {
    window.videojs = videojs;
  }

  if (props.cast) {
    await insertScript(getMediaUrl("assets/js", "silvermine-videojs-airplay.min.js"));
    await insertScript(getMediaUrl("assets/js", "silvermine-videojs-chromecast.min.js"));
    await insertScript("https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1");
  }

  if (videoSourceType.value === "video/mp4") {
    await insertScript(getMediaUrl("assets/js", "videojs-vr.js"));
  }

  await nextTick();
  load();
};

const getSubtitles = (name: string) => {
  if (name === "") {
    return "";
  }

  name = `${name.split(".").slice(0, -1).join(".")}.vtt`;
  // todo: workaround for subtitles on ios due to cors problem
  const subtitleHost = import.meta.env.VITE_ENV === ENV_PRODUCTION ? location.origin : import.meta.env.VITE_MEDIA_CONTENT_HOST;
  return `${subtitleHost}/content/subtitles/${name}`;
};

const setChromecastOptions = (options: Record<string, any>) => {
  options.techOrder.unshift("chromecast");
  options.chromecast = {
    modifyLoadRequestFn: (loadRequest: any) => {
      const tracks = [];

      for (let i = 0; i < subtitles.value.length; i++) {
        const track = new window.chrome.cast.media.Track(i, window.chrome.cast.media.TrackType.TEXT);
        track.trackContentId = subtitles.value[i].src;
        track.trackContentType = "text/vtt";
        track.subtype = window.chrome.cast.media.TextTrackType.CAPTIONS;
        track.name = subtitles.value[i].label;
        track.language = subtitles.value[i].lang;
        tracks.push(track);
      }

      loadRequest.activeTrackIds = currentTextTrackIndex.value !== null ? [currentTextTrackIndex.value] : [];
      loadRequest.media.tracks = tracks;

      return loadRequest;
    },
  };
  options.plugins.chromecast = {};

  return options;
};

const setMuxOptions = (options: Record<string, any>) => {
  const cdn = videoSource.value.indexOf("cdn-cf") ? "cloudflare" : "cdn77";
  options.plugins.mux = {
    debug: false,
    minimumRebufferDuration: 250,
    data: {
      env_key: import.meta.env.VITE_MUX_KEY,
      viewer_user_id: profile.value?.id || "",
      video_title: props.id,
      player_init_time: Date.now(),
      video_cdn: cdn,
    },
  };

  return options;
};

onBeforeUnmount(() => {
  if (player.value && props.id && props.id !== activeVideo.value) {
    player.value.dispose();
  }

  window.removeEventListener("beforeunload", sendPlayback);

  sendPlayback();
});
</script>

<style lang="scss">
@import "video.js/dist/video-js.css";
@import "videojs-mobile-ui/dist/videojs-mobile-ui.css";
@import "@/assets/silvermine/silvermine-videojs-chromecast.css";
@import "@/assets/silvermine/silvermine-videojs-airplay.css";
@import "./index";
</style>
